import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Iconify from 'src/components/Iconify';

// Compact banner header for allocation-action pop-ups — same look as the
// "Update Blacklist" dialog (tinted strip, circular icon, title + subtitle) but
// slim. The subtitle carries the context: project number · person name, so the
// PM always sees which project and which linguist/formatter the action targets.
//
// `title` is the action name (e.g. "Edit Allocation"). Colour + icon are picked
// automatically from the title (delete=red, confirm/complete=green, …) and can
// be overridden with `color` / `icon`. `projectNo` should be already prefixed
// (e.g. "LY/1234", "CI/2297").

const VARIANTS = [
  { test: /delete|remove/, color: 'error', icon: 'eva:trash-2-outline' },
  { test: /confirm/, color: 'success', icon: 'eva:checkmark-circle-2-outline' },
  { test: /complete/, color: 'success', icon: 'eva:done-all-outline' },
  { test: /edit/, color: 'primary', icon: 'eva:edit-2-outline' },
  { test: /review/, color: 'warning', icon: 'eva:file-text-outline' },
  { test: /skip/, color: 'warning', icon: 'eva:skip-forward-outline' },
  { test: /send|booking/, color: 'info', icon: 'eva:paper-plane-outline' }
];
const DEFAULT_VARIANT = { color: 'primary', icon: 'eva:briefcase-outline' };

const variantFor = (title = '') => {
  const t = String(title).toLowerCase();
  return VARIANTS.find((v) => v.test.test(t)) || DEFAULT_VARIANT;
};

AllocationInfoHeader.propTypes = {
  title: PropTypes.string,
  projectNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  linguistName: PropTypes.string,
  subtitle: PropTypes.string,
  color: PropTypes.string,
  icon: PropTypes.string,
  sx: PropTypes.object
};

export default function AllocationInfoHeader({
  title,
  projectNo,
  linguistName,
  subtitle,
  color,
  icon,
  sx
}) {
  const v = variantFor(title);
  const tone = color || v.color;
  const glyph = icon || v.icon;
  const context =
    subtitle || [projectNo, linguistName].filter(Boolean).join('  ·  ');
  if (!title && !context) return null;

  return (
    <Box
      sx={(theme) => ({
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2.5,
        py: 1.5,
        bgcolor: alpha(
          (theme.palette[tone] || theme.palette.primary).main,
          0.08
        ),
        ...sx
      })}
    >
      <Box
        sx={(theme) => ({
          width: 36,
          height: 36,
          flexShrink: 0,
          borderRadius: '50%',
          display: 'grid',
          placeItems: 'center',
          color: `${tone}.main`,
          bgcolor: alpha(
            (theme.palette[tone] || theme.palette.primary).main,
            0.16
          )
        })}
      >
        <Iconify icon={glyph} sx={{ width: 18, height: 18 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        {title && (
          <Typography variant="subtitle1" sx={{ lineHeight: 1.2 }} noWrap>
            {title}
          </Typography>
        )}
        {context && (
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: 600, color: 'text.primary' }}
          >
            {context}
          </Typography>
        )}
      </Box>
    </Box>
  );
}
